import React, { lazy, ReactElement, useContext, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { useRouter } from 'next/router';
import find from 'lodash/find';
import { toggleModal, toggleShowMobileMenu } from '@Hotels-at-Home/hah-fe-core/dist/store/modal';
import { selectShowModal } from '@Hotels-at-Home/hah-fe-core/dist/storeSelectors/modal';
import {
  useMarriottAdobeLaunch,
  useAnalyticsScripts,
  useGetPageClass,
  useConsentModeListener,
  useInterval,
  useFirstVisit,
  useWindowSize,
  useScrollRestoration,
  useCatalogId,
  useRumScript,
} from '@Hotels-at-Home/hah-fe-core/dist/hooks';
import {
  COOKIE_CHECKOUT_START,
  COOKIE_CURRENCY_NAME,
  COOKIE_LANGUAGE_NAME,
  LanguageCode,
  CurrencyCode,
} from '@Hotels-at-Home/hah-fe-core/dist/constants';
import Header from '@Hotels-at-Home/hah-fe-core/dist/components/Header';
import SiteLinks from '@Hotels-at-Home/hah-fe-core/dist/components/Header/SiteLinks';
import SearchDrawer from '@Hotels-at-Home/hah-fe-core/dist/components/SearchDrawer';
import ShoppingCartDrawer from '@Hotels-at-Home/hah-fe-core/dist/components/CartDrawer';
import {
  ActionType,
  IFooter,
  INavMenu,
  ModalType,
  IGetPromotionResponse,
  UtilityIcons,
} from '@Hotels-at-Home/hah-fe-core/dist/types';

import { useDispatch, useSelector } from '~/store/hooks';
import {
  getCookie,
  getCookies,
  IConsentMode,
  removeCookie,
  setPreferredLangCookie,
  setFirstVisitCookie,
} from '@Hotels-at-Home/hah-fe-core/dist/utils/cookies';
import { CookieRestorer } from '@Hotels-at-Home/hah-fe-core/dist/utils/cookieRestorer';
import { BonvoyAuthContext } from '@Hotels-at-Home/hah-fe-core/dist/context/auth';
import Footer from '../components/layouts/Footer';
import {
  BannerWrapper,
  CurrencyDrawer,
  LanguageDrawer,
  AuthSidebar,
} from '@Hotels-at-Home/hah-fe-core/dist/components';
import Modal from '@Hotels-at-Home/hah-fe-core/dist/components/Modal';
import BmsmChart from '../components/common/Banner/BmsmChart';

import classnames from 'classnames';

import { getHrefLinks } from '~/utils/hreflangs';
import { selectProduct } from '@Hotels-at-Home/hah-fe-core/dist/storeSelectors/product';
import { selectCategory } from '@Hotels-at-Home/hah-fe-core/dist/storeSelectors/category';
import { selectCurrentCurrencyCode } from '@Hotels-at-Home/hah-fe-core/dist/storeSelectors/currencies';
import { getTermsAndConditionsByCurrency } from '@Hotels-at-Home/hah-fe-core/dist/utils/promotion';
import { useTranslation } from 'next-i18next';

import {
  cloudWatchRum,
  employeePromoTag,
  cookieRestorerEnabled,
  cookieRestorerOptions,
} from '~/constants/siteConfig';
import { siteLinks, bonvoyLogo } from '~/constants/siteLinks';

const FirstVisitModal = lazy(
  () => import('@Hotels-at-Home/hah-fe-core/dist/components/FirstVisitModal'),
);

interface IProps {
  userAgent: string;
  children: React.ReactNode;
  navMenu: INavMenu;
  footer: IFooter;
  promotion: IGetPromotionResponse;
  networkErrorStatusCode: number;
  isSafari: boolean;
}

const AppLayout = ({
  userAgent,
  children,
  navMenu,
  footer,
  promotion,
  networkErrorStatusCode,
  isSafari,
}: IProps): ReactElement => {
  useCatalogId();
  useScrollRestoration();
  const { pathname, route } = useRouter();
  const [consentMode, setConsentMode] = useState<IConsentMode | null>(null);
  const [shouldIntervalBeCancelled, setShouldIntervalBeCancelled] = useState(false);
  const [isFirstVisitModalVisible, setIsFirstVisitModalVisible] = useState(false);

  const windowSize = useWindowSize();
  const smallScreenThreshold = 768;
  const isSmallScreen = Boolean(windowSize.width) && windowSize.width <= smallScreenThreshold;
  const { isBonvoyLoggedIn } = useContext(BonvoyAuthContext);

  useConsentModeListener((consent: any) => {
    setConsentMode(consent);
  });

  const currencyCookie = getCookie(COOKIE_CURRENCY_NAME) as string;
  const lang = getCookie(COOKIE_LANGUAGE_NAME) as LanguageCode;

  useAnalyticsScripts();
  useRumScript(cloudWatchRum);
  useMarriottAdobeLaunch(process.env.NEXT_PUBLIC_ADOBE_LAUNCH_URL);

  const dispatch = useDispatch();
  const showModal = useSelector(selectShowModal);
  const product = useSelector(selectProduct);
  const { category } = useSelector(selectCategory);
  const currentCurrency = useSelector(selectCurrentCurrencyCode);
  const isValidCurrency = Object.values(CurrencyCode).includes(currencyCookie as CurrencyCode);
  const currency = currentCurrency ?? (isValidCurrency ? currencyCookie : CurrencyCode.EUR);

  const { t } = useTranslation('', { keyPrefix: '' });

  useEffect(() => {
    gsap.config({ nullTargetWarn: false });
  }, []);

  const cookies = getCookies();

  const pageClass = useGetPageClass();

  const termsAndConditionsByCurrency: string = getTermsAndConditionsByCurrency(
    promotion?.promotion_terms,
    currency,
  );

  const handleToggleModal = (modal: ModalType) => {
    dispatch(toggleModal(modal));
  };

  const onAction = (action: ActionType) => {
    let modalType;

    switch (action) {
      case ActionType.Account: {
        modalType = ModalType.Account;
        break;
      }
      case ActionType.Search: {
        modalType = ModalType.Search;
        break;
      }
      case ActionType.Language: {
        modalType = ModalType.Language;
        break;
      }
      case ActionType.Currency: {
        modalType = ModalType.Currency;
        break;
      }
      case ActionType.ShoppingCart: {
        modalType = ModalType.ShoppingCart;
        break;
      }
      default: {
        break;
      }
    }

    handleToggleModal(modalType);
  };

  const closeHandler = () => {
    if (showModal) {
      const idString = `${showModal.toLocaleLowerCase()}-button`;
      const actionButton = document.getElementById(idString);
      if (actionButton) {
        actionButton.focus();
      }
    }
    dispatch(toggleModal(null));
    dispatch(toggleShowMobileMenu(null));
  };

  const removeCheckoutCookies = () => {
    if (!pathname.includes('checkout') && cookies[COOKIE_CHECKOUT_START]) {
      removeCookie(COOKIE_CHECKOUT_START);
    }
  };

  const { isFirstVisitModalOpened, setIsFirstVisitModalOpened, handleClose } = useFirstVisit();

  const handleScroll = () => {
    if (window.scrollY > 1000) {
      setIsFirstVisitModalVisible(true);
      window.removeEventListener('scroll', handleScroll); // Remove the event listener after triggering the modal
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll); // Clean up the event listener on component unmount
    };
  }, []);

  useEffect(() => {
    if (!cookieRestorerEnabled || !isSafari) return;

    CookieRestorer(cookieRestorerOptions);

    const interval = setInterval(() => {
      CookieRestorer(cookieRestorerOptions);
    }, cookieRestorerOptions.options.interval || 5000);

    // eslint-disable-next-line consistent-return
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!cookieRestorerEnabled || !isSafari) return;
    CookieRestorer(cookieRestorerOptions);
    const interval = setInterval(() => {
      CookieRestorer(cookieRestorerOptions);
    }, cookieRestorerOptions.options.interval || 5000);
    // eslint-disable-next-line consistent-return
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    removeCheckoutCookies();
  }, [pathname]);

  const isHomepage = !route.includes('/p/') && !route.includes('/c/');

  // Pages that are neither the homepage nor cat/prod, but which core still
  // renders with its "homepage" banner variant
  const isMiscPages =
    route.includes('/404') ||
    route.includes('/search') ||
    route.includes('/cart') ||
    route.includes('/help');

  const hrefLinks = getHrefLinks(isHomepage, product, category);

  const checkWindowConsentMode = () => {
    const consentCookie = getCookie('CookieConsent') as IConsentMode;

    if (consentCookie) {
      const consent: IConsentMode = consentCookie;

      if (consent) {
        setConsentMode(consent);

        setShouldIntervalBeCancelled(true);
      }
    }

    if (
      window?.CookieConsent?.consent !== undefined &&
      Object.values(window.CookieConsent.consent).every(e => e)
    ) {
      setFirstVisitCookie(userAgent, window.CookieConsent.consent);

      setConsentMode(window.CookieConsent.consent);

      setShouldIntervalBeCancelled(true);
    }
    if (
      window?.ConsentMode?.consent !== undefined &&
      Object.values(window.ConsentMode.consent).every(e => e)
    ) {
      setConsentMode(window.ConsentMode.consent);
      setShouldIntervalBeCancelled(true);
    }
  };

  useInterval(
    () => {
      checkWindowConsentMode();
    },
    shouldIntervalBeCancelled ? null : 1000,
  );

  useEffect(() => {
    if (consentMode) {
      setPreferredLangCookie(userAgent, currency, lang);
    }
  }, [consentMode]);

  /* Banner logic */

  const [shouldHideBanner, setShouldHideBanner] = useState<boolean | null>(null);

  useEffect(() => {
    const promoTag = getCookie('promo-tag');
    const hideForPromoTag = promoTag === employeePromoTag;
    setShouldHideBanner(hideForPromoTag);
  }, []);

  const [showBmsmLightbox, setShowBmsmLightbox] = useState(false);

  const isBmsm = promotion?.promo_type === 'bmsm';
  const bannersAreVisible = shouldHideBanner === false && promotion?.promo_is_active;

  const openBmsmLightbox = () => setShowBmsmLightbox(true);
  const closeBmsmLightbox = () => setShowBmsmLightbox(false);

  const handleBmsmKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      openBmsmLightbox();
    }
  };

  const renderBanners: ReactElement | null = (() => {
    if (!bannersAreVisible) {
      return null;
    }

    if (!isBmsm) {
      return <BannerWrapper promotion={promotion} />;
    }

    // BMSM runs no header banner on the homepage - the chart lives in the hero
    // instead. Everywhere else the banner opens the chart in a lightbox.
    if (isHomepage && !isMiscPages) {
      return null;
    }

    return (
      <>
        <div
          role="button"
          tabIndex={0}
          aria-label={t('$local.$bmsmChart.title')}
          className="bmsm-banner-wrapper"
          onClick={openBmsmLightbox}
          onKeyDown={handleBmsmKeyDown}
        >
          <BannerWrapper promotion={promotion} />
        </div>
        <Modal
          open={showBmsmLightbox}
          maxWidth="sm"
          className="base-banner-lightbox bmsm-lightbox"
          onClose={closeBmsmLightbox}
        >
          <div className="content">
            <BmsmChart
              promotion={promotion}
              daysLeft={promotion.promo_days_left}
              ends={new Date(promotion.promo_end).toLocaleDateString('en-US', { weekday: 'long' })}
            />
          </div>
        </Modal>
      </>
    );
  })();

  return (
    <div
      id="app"
      className={classnames(
        {
          safari: isSafari,
          'body-font-loaded': true,
          'heading-font-loaded': true,
        },
        pageClass,
      )}
    >
      <SiteLinks items={siteLinks} logo={bonvoyLogo} />
      <Header
        navMenu={navMenu}
        showActions={!networkErrorStatusCode}
        onAction={onAction}
        transparent
        sticky
        disableStyckyAnimation
        iconOrder={[
          UtilityIcons.ShoppingCart,
          UtilityIcons.Language,
          UtilityIcons.Currency,
          UtilityIcons.Search,
          UtilityIcons.Account,
        ]}
      />
      {renderBanners}
      <main id="content" className="content">
        {children}
      </main>
      <Footer
        footer={footer}
        termsAndConditions={termsAndConditionsByCurrency}
        promotion={promotion}
      />
      <SearchDrawer isOpen={showModal === ModalType.Search} onClose={closeHandler} />
      <CurrencyDrawer isOpen={showModal === ModalType.Currency} onClose={closeHandler} />
      <LanguageDrawer
        enableNA
        enableAPAC
        hrefLinks={hrefLinks}
        isOpen={showModal === ModalType.Language}
        onClose={closeHandler}
      />
      <AuthSidebar
        isLogged={isBonvoyLoggedIn}
        isOpen={showModal === ModalType.Account}
        onClose={closeHandler}
      />
      <ShoppingCartDrawer isOpen={showModal === ModalType.ShoppingCart} onClose={closeHandler} />
      {isFirstVisitModalOpened && !isSmallScreen && isFirstVisitModalVisible && (
        <FirstVisitModal
          onClose={handleClose}
          privacyPolicy={find(footer?.bottomFooterLinks, { name: 'privacy-policy' })}
          subtitle={t('$local.firstVisitSubtitle')}
          enableScrollingBackground
        />
      )}
    </div>
  );
};

export default AppLayout;
